import * as React from "react";
import { Editor, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";

import { IconsEnum } from "components/icon";
import { trackTextFormattingClick } from "components/message-box/analytics";
import {
  RichBlockFormatType,
  RichTextFormatType,
  isBlockActive,
  isMarkActive,
} from "components/rich-editor/plugins/richText";
import { ToolbarIcon, ToolbarSplit } from "components/toolbar";
import ImagesIcon from "components/toolbar/ImagesIcon";
import { isMac } from "helpers/isMac";
import { isMobile } from "helpers/isMobile";

import { StyledIconsWrapper, StyledSecondaryToolbarContainer } from "../styles";

const shortcutTooltip = (shortcut: string, label: string): string => {
  if (isMobile(navigator.userAgent)) {
    return label;
  }

  let platformShortcut;
  if (isMac(navigator.userAgent)) {
    platformShortcut = shortcut.replace(/mod/, "⌘");
  } else {
    platformShortcut = shortcut.replace(/mod/, "Ctrl");
  }

  return `${label} (${platformShortcut})`;
};

export interface ITextFormattingToolbarProps {
  mobile: boolean;
  privateMode: boolean;
  disabled: boolean;
  onLinkInsert: React.EventHandler<React.MouseEvent<HTMLDivElement>>;
}

const TextFormattingToolbar: React.FC<ITextFormattingToolbarProps> = ({
  mobile,
  privateMode,
  disabled,
  onLinkInsert,
}) => {
  const editor = useSlate();

  const isActiveText = (format: RichTextFormatType): boolean => isMarkActive(editor, format);
  const isActiveBlock = (format: RichBlockFormatType): boolean => isBlockActive(editor, format);

  const handleTextFormat =
    (format: RichTextFormatType) =>
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      trackTextFormattingClick(format);
      event.preventDefault();

      if (!ReactEditor.isFocused(editor)) {
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.start(editor, [0]));
      }

      const isActive = isMarkActive(editor, format);
      if (isActive) {
        editor.removeMark(format);
      } else {
        editor.addMark(format, true);
      }
    };

  const handleBlockFormat =
    (format: RichBlockFormatType) =>
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      trackTextFormattingClick(format);
      event.preventDefault();

      if (!ReactEditor.isFocused(editor)) {
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.start(editor, [0]));
      }

      editor.formatBlock(format);
    };

  const handleLinkInsert = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    trackTextFormattingClick("link");
    onLinkInsert(event);
  };

  return (
    <StyledSecondaryToolbarContainer>
      <StyledIconsWrapper>
        <ToolbarIcon
          testSelector="format-bold"
          icon={IconsEnum.FormatBold}
          active={isActiveText("bold")}
          disabled={disabled}
          tooltip={shortcutTooltip("mod+B", "Bold")}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={handleTextFormat("bold")}
        />
        <ToolbarIcon
          testSelector="format-italic"
          icon={IconsEnum.FormatItalic}
          active={isActiveText("italic")}
          disabled={disabled}
          tooltip={shortcutTooltip("mod+I", "Italic")}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={handleTextFormat("italic")}
        />
        <ToolbarIcon
          testSelector="format-underline"
          icon={IconsEnum.FormatUnderline}
          tooltip={shortcutTooltip("mod+U", "Underline")}
          active={isActiveText("underline")}
          disabled={disabled}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={handleTextFormat("underline")}
        />
        <ToolbarIcon
          testSelector="format-strikethrough"
          icon={IconsEnum.FormatStrikethrough}
          active={isActiveText("strikethrough")}
          tooltip={shortcutTooltip("mod+Shift+X", "Strikethrough")}
          disabled={disabled}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={handleTextFormat("strikethrough")}
        />
        <ToolbarIcon
          testSelector="format-code"
          icon={IconsEnum.Code}
          active={isActiveText("code")}
          tooltip={shortcutTooltip("mod+Shift+C", "Code")}
          onMouseDown={handleTextFormat("code")}
          privateMode={privateMode}
          mobile={mobile}
          disabled={disabled}
        />
        <ToolbarSplit privateMode={privateMode} />
        <ToolbarIcon
          testSelector="format-numbered-list"
          icon={IconsEnum.FormatListNumbered}
          active={isActiveBlock("numbered-list")}
          tooltip={shortcutTooltip("mod+Shift+8", "Numbered List")}
          disabled={disabled}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={handleBlockFormat("numbered-list")}
        />
        <ToolbarIcon
          testSelector="format-bulleted-list"
          icon={IconsEnum.FormatListBulleted}
          active={isActiveBlock("bulleted-list")}
          disabled={disabled}
          tooltip={shortcutTooltip("mod+Shift+7", "Bulleted List")}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={handleBlockFormat("bulleted-list")}
        />
        <ToolbarSplit privateMode={privateMode} />
        <ToolbarIcon
          testSelector="insert-link"
          icon={IconsEnum.Link}
          active={false}
          disabled={disabled}
          tooltip={shortcutTooltip("mod+K", "Link")}
          privateMode={privateMode}
          mobile={mobile}
          onMouseDown={handleLinkInsert}
        />
        <ImagesIcon mobile={mobile} privateMode={privateMode} disabled={disabled} tooltip="Insert Image" />
      </StyledIconsWrapper>
    </StyledSecondaryToolbarContainer>
  );
};

export default TextFormattingToolbar;
